import { Component, OnDestroy, OnChanges, SimpleChanges, AfterViewInit, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs/Rx';
import { NavigationService } from '../../services/navigation.service';
import { MessageService } from 'base';
import { NetFacadeService } from '../../services/netfacade.service';
import { NetViewDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { CredentialsComponent } from 'credentials';
import { EmailSettingsComponent, SlackSettingsComponent } from 'configuration';
import { ChangeableContainer } from '../ChangeableContainer';
import { NetworkSettingsComponent } from '../network-settings/network-settings.component';
import { DeviceFilterListComponent } from '../device-filter-list/device-filter-list.component';
import { MapDrawSettingsComponent } from 'configuration';
import { DiscResultsService } from 'discovery';
import { NvtnPrtgExportConfigComponent } from '../nvtn-prtg-export-config/nvtn-prtg-export-config.component';
import { IdentityService, LicensingService, UserPermissionsService } from 'identity';
import { DevicesComponent } from 'netview';
import { ReportsComponent } from 'infosets';


@Component({
    selector: 'network',
	templateUrl: './network.component.html',
	styleUrls: ['./network.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class NetworkComponent extends ChangeableContainer implements OnDestroy, AfterViewInit {

	public network: NetViewDto;
	private locationChangedSub: Subscription;
	public selectedTab: number;

	@ViewChild(DevicesComponent)
	public devices: DevicesComponent;

	@ViewChild(ReportsComponent)
	public reports: ReportsComponent;

    @ViewChild(NetworkSettingsComponent)
	public networkSettings: NetworkSettingsComponent;

    @ViewChild(CredentialsComponent)
	public credentials: CredentialsComponent;

    @ViewChild(EmailSettingsComponent)
	public emailSettings: EmailSettingsComponent;

	@ViewChild(SlackSettingsComponent)
	public slackSettings: SlackSettingsComponent;

    @ViewChild(DeviceFilterListComponent)
	public deviceFilterList: DeviceFilterListComponent;

	// The device group tree is stored external from the netview.
	// Therefore, device group changes do not require saving the netview.
	// Therefore, the device group list is not included in the "children"
	// list.  The disadvantage of this approach is that temporary changes
	// made to a device group are not automatically saved if the user
	// navigates to another network before saving.  The advantage of this
	// approach is that temporary device group changes do not cause
	// the netview to be unnecessarily saved when the user navigates to
	// another network.
	//@ViewChild(DeviceGroupListComponent)
	//public deviceGroupList: DeviceGroupListComponent;

    @ViewChild(MapDrawSettingsComponent)
	public mapSettings: MapDrawSettingsComponent;

    @ViewChild(NvtnPrtgExportConfigComponent)
	public prtgExport: NvtnPrtgExportConfigComponent;

	public readonly DevicesTabIndex = 0;
	public readonly ReportsTabIndex = 1;
	public readonly EventsTabIndex = 2;
	public get MonitorsTabIndex(): number {
		return (this.canViewCredentials ? 10 : 9);
	}

	constructor(
		navService: NavigationService,
		messageService: MessageService,
		private netFacadeService: NetFacadeService,
		private identityService: IdentityService,
		private userPermissionsService: UserPermissionsService) {

		super(navService, messageService);

		this.selectedTab = 1;

		this.getCurrentNetwork();
		this.locationChangedSub = this.navService.onLocationChanged.subscribe(() => {
			this.getCurrentNetwork();
		});
	}

	public ngAfterViewInit(): void {
		this.children = [
			this.networkSettings,
			this.credentials,
			this.emailSettings,
			this.slackSettings,
			this.deviceFilterList,
			this.mapSettings,
			this.prtgExport,
		];
	}

	public ngOnDestroy(): void {
		if (this.locationChangedSub) {
			this.locationChangedSub.unsubscribe();
			this.locationChangedSub = undefined;
		}
	}

	public get isLoggedIn(): boolean {
		return this.identityService.isLoggedIn;
	}

	public get canViewCredentials(): boolean {
		return this.userPermissionsService.canViewCredentials;
	}

	private getCurrentNetwork(): void {
		this.network = null;
		
		if (this.navService.location.hasNetViewGuid) {
			let networkGuid = this.navService.location.netViewGuid;
			this.network = this.netFacadeService.findNetwork(networkGuid);
		}
	}

	public isSaveEnabled(): boolean {
		return (this.network && (this.network.HasChanges || this.tempChanges));
	}

	protected save(): void {
		this.messageService.displayInfoMessage(`Saving changes to network '${this.network.Name}'`);
		// Make local copy of network reference because the network loaded in this component
		// might change before the asynchronous save operation completes
		let network: NetViewDto = this.network;
		this.netFacadeService.updateNetView(network).subscribe(
			(result: NetViewDto) => {
				network.HasChanges = false;
			},
			(error: any) => {
				this.messageService.displayErrorMessage("Saving network changes failed");
			});
	}

	public onTabChange(event: any): void {
		this.selectedTab = event.index;

		switch (this.selectedTab) {	
			case this.DevicesTabIndex:
				if (this.devices) {
					this.devices.reloadDeviceList();
				}
				break;
			case this.ReportsTabIndex:
				if (this.reports) {
					this.reports.reloadReport();
				}
				break;
		}
	}

}
